import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { ValidationForm } from "../ValidationForm";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  CustomInput,
  FormGroup,
  Input,
  Label,
  ListGroup,
  ListGroupItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import AccessoryProgress from "./AccessoryProgress";
import { useAccessory } from "../../context/AccessoryContext";
import SuggestiveInput from "../SuggestiveInput";
import { useService } from "../../context/ServiceContext";
import ProductInfo from "../ProductCreation/ProductInfo";
import { replaceBlockedCharacters } from "../../products/ProductValidation";
import { MultiSelect } from "react-multi-select-component";
import { fireAlert } from "../../utils/alerts";
import { ErrorSummary } from "./AccessorySummary";

const magnitudes = {
  1: "m3/h",
  2: "m3/s",
  6: "Pa",
  29: "m/s",
};

function AccessoryInformation({ progressItems, onNext, onPrevious }) {
  const intl = useIntl();
  const {
    productSAPSuggester,
    accessoryCategoriesProvider,
    accessoryExistenceProvider,
    accessoryExistenceBdPProvider,
    accessoryDescriptonAccessor,
    accessoryClassProductsProvider,
    manufacturerCompaniesProvider,
    voltagesProvider,
    newAccessoryClassRecorder,
  } = useService();

  const { accessory, updateAccessory, isNewAccessory, getErrors } = useAccessory();
  const { series } = accessory;
  const [errors, setErrors] = useState(null);
  const isReadOnly = accessory?.isReadOnly || false;

  const [accessoryExists, setAccessoryExists] = useState("");
  const [accessoryExistsInBdP, setAccessoryExistsInBdP] = useState(null);
  const [accessoryNotFound, setAccessoryNotFound] = useState(false);
  // const [hasPressureLosses, setHasPressureLosses] = useState(false);
  const [searchSAP, setSearchSAP] = useState("");
  const [searchSAPSuggestion, setSearchSAPSuggestion] = useState(null);

  const [companies, setCompanies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [availableVoltages, setAvailableVoltages] = useState([]);
  const [productsWithSameClass, setProductsWithSameClass] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [accessoryClass, setAccessoryClass] = useState("");
  const [newAccessoryClass, setNewAccessoryClass] = useState("");
  const [voltages, setVoltages] = useState([]);

  const toggleModal = () => setModalOpen(!modalOpen);

  const localisedStrings = {
    allItemsAreSelected: "All items are selected.",
    clearSearch: "Clear Search",
    clearSelected: "Clear Selected",
    noOptions: "No options",
    search: "Search",
    selectAll: "Select All",
    selectAllFiltered: "Select All (Filtered)",
    selectSomeItems: intl.formatMessage({ id: "product_information.select_an_option", defaultMessage: "Please select an option" }),
  };

  function relationshipAlreadyExists() {
    const cleanSearchSAP = searchSAP.trim();
    return accessory.codAccesorioComercial == cleanSearchSAP || accessory.newClassGroups.some((c) => c.codProductoComercial == cleanSearchSAP);
  }

  const handleAddRelation = async () => {
    setNewAccessoryClass("");

    if (relationshipAlreadyExists()) {
      fireAlert(
        intl.formatMessage({
          id: "accessory_information.code_already_added",
          defaultMessage: "You have already added this code",
        }),
        intl.formatMessage({ id: "app.error", defaultMessage: "Error" }),
        "error"
      );
      return;
    }

    let product = null;

    if (!product) {
      const result = await productSAPSuggester.getSuggestions(searchSAP);
      product = result?.find((r) => r.code == searchSAP || r.description == searchSAP);
    }

    if (!product) {
      setSelectedProduct(null);
      fireAlert(
        intl.formatMessage({
          id: "accessory_information.product_not_found",
          defaultMessage: "Please check the entered product code.",
        }),
        intl.formatMessage({ id: "app.error", defaultMessage: "Error" }),
        "error"
      );
      return;
    }

    setSelectedProduct(product);
    try {
      const response = await accessoryClassProductsProvider.get(searchSAP);
      setProductsWithSameClass(response);

      const existsInBdp = await accessoryExistenceBdPProvider.get(searchSAP);
      setAccessoryExistsInBdP(existsInBdp.exists);

      if (!existsInBdp.exists) {
        setNewAccessoryClass(product.code);
      }

      setModalOpen(true);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const handleModalConfirm = () => {
    if (selectedProduct) {
      const newRelationship = {
        codProductoComercial: selectedProduct.code,
        productoComercial: selectedProduct.description,
        claseAccesorio: accessoryClass || newAccessoryClass,
      };

      //setProductRelationships([...productRelationships, newRelationship]);
      update("newClassGroups", [...accessory.newClassGroups, newRelationship]);
      setAccessoryClass("");
      setSelectedProduct(null);
      setSearchSAPSuggestion(null);
      setSearchSAP("");
      setProductsWithSameClass([]);
      toggleModal();
    }
  };

  const handleRemoveRelationship = (relationship) => {
    update(
      "newClassGroups",
      accessory.newClassGroups.filter((rel) => rel.codProductoComercial !== relationship.codProductoComercial)
    );
  };

  useEffect(() => {
    async function getData() {
      try {
        const categories = await accessoryCategoriesProvider.get();
        const companies = await manufacturerCompaniesProvider.get();
        const voltages = await voltagesProvider.get();

        setAvailableVoltages(
          voltages.map((a) => {
            return {
              label: a.value,
              value: a.id,
            };
          }) ?? []
        );
        setCompanies(companies);
        setCategories(categories);

        console.log(accessory?.voltages);
      } catch (error) {
      } finally {
      }
    }
    getData();
  }, []);

  useEffect(() => {
    if (availableVoltages?.length > 0 && accessory?.id != undefined && accessory.voltages?.length > 0) {
      setVoltages(accessory.voltages.map((v) => availableVoltages.find((av) => av.value == v)));
    }
  }, [accessory?.voltages, availableVoltages]);

  async function validate(e) {
    e.preventDefault();

    if (isReadOnly) {
      onNext();
      return;
    }

    update(
      "voltages",
      voltages.map((v) => v.value)
    );

    const errors = await getErrors(accessory);
    setErrors(errors);
    if (!errors.hasErrors) {
      onNext();
    } else {
      fireAlert(<ErrorSummary intl={intl} errors={errors}></ErrorSummary>, "Incorrect attributes", "error");
    }
  }

  function update(property, value) {
    updateAccessory({ ...accessory, [property]: value });
  }

  const getSelectedSwitch = () => {
    if (accessory.flagCommutador2 === 1 && accessory.flagCommutador3 === 1) {
      return "both";
    } else if (accessory.flagCommutador2 === 1) {
      return "switch2";
    } else if (accessory.flagCommutador3 === 1) {
      return "switch3";
    }
    return "";
  };

  const handleSwitchChange = (event) => {
    const value = event.target.value;
    let updatedData;

    switch (value) {
      case "switch2":
        updatedData = { ...accessory, flagCommutador2: 1, flagCommutador3: 0 };
        break;
      case "switch3":
        updatedData = { ...accessory, flagCommutador2: 0, flagCommutador3: 1 };
        break;
      case "both":
        updatedData = { ...accessory, flagCommutador2: 1, flagCommutador3: 1 };
        break;
      default:
        updatedData = { ...accessory, flagCommutador2: 0, flagCommutador3: 0 };
    }

    updateAccessory(updatedData);
  };

  async function lookupAccessoryDescription() {
    const result = await accessoryExistenceProvider.get(accessory.codAccesorioComercial);
    if (result?.exists) {
      if (accessoryExists != accessory.codAccesorioComercial) {
        fireAlert(
          intl.formatMessage({ id: "accessories.accessory_already_exists", defaultMessage: "Accessory code already exists" }),
          intl.formatMessage({ id: "app.error", defaultMessage: "Error" }),
          "error"
        );
      }
      setAccessoryExists(accessory.codAccesorioComercial);
      update("productoAccesorioComercial", "");
      return;
    }
    setAccessoryExists("");
    const description = await accessoryDescriptonAccessor.find(accessory.codAccesorioComercial);
    update("productoAccesorioComercial", description?.productCodeDescription || "");
    setAccessoryNotFound(description == null || !description.productCodeDescription);
  }

  async function save() {}

  const customValueRenderer = (selected, _options) => {
    return selected.map(({ label }) => label).join(",");
  };

  async function showNewAccessoryClass() {
    await newAccessoryClassRecorder.store({
      codAccesorioComercial: accessory.codAccesorioComercial ?? "",
      codProductoComercial: selectedProduct.code ?? "",
      productAvailableInDB: accessoryExistsInBdP,
    });
    fireAlert(
      intl.formatMessage({ id: "app.functionality_unavailable", defaultMessage: "This functionality is not available at the moment." }),
      intl.formatMessage({ id: "app.information", defaultMessage: "Information" }),
      "information"
    );
  }

  return (
    <ValidationForm onSubmit={validate}>
      <Row>
        <Col xl={3} className="d-sm-none d-xl-block">
          <AccessoryProgress onNext={() => {}} onPrevious={onPrevious} items={progressItems}></AccessoryProgress>
        </Col>
        <Col xl={9}>
          <Card>
            <CardHeader>
              <FormattedMessage id="accessory_information.title" defaultMessage="Please enter the accessory information" />
            </CardHeader>
            <CardBody className="p-3">
              <FormGroup row className="px-4">
                <Label sm={3}>
                  <FormattedMessage id="attribute.idCompanyManufacturer" defaultMessage="Manufacturing Company" />
                  <ProductInfo formattedMessage={{ id: "attribute_info.idCompanyManufacturer", defaultMessage: "attribute_info.idCompanyManufacturer" }}></ProductInfo>
                </Label>
                <Col sm={9}>
                  {isReadOnly ? (
                    <Input type="text" plaintext readOnly value={accessory.idCompanyManufacturer} />
                  ) : (
                    <Input type="select" id="idCompanyManufacturer" value={accessory.idCompanyManufacturer || ""} onChange={(e) => update("idCompanyManufacturer", e.target.value)}>
                      {companies.map((company) => (
                        <option key={company.id} value={company.id}>
                          {company.value}
                        </option>
                      ))}
                    </Input>
                  )}
                </Col>
              </FormGroup>
              <FormGroup row className="px-4">
                <Label sm={3}>
                  <FormattedMessage id="attribute.active" defaultMessage="Active" />
                  <ProductInfo formattedMessage={{ id: "attribute_info.active", defaultMessage: "attribute_info.active" }}></ProductInfo>
                </Label>
                <Col sm={3}>
                  {isReadOnly ? (
                    <Input type="text" plaintext readOnly value={accessory.activo} />
                  ) : (
                    <CustomInput
                      type="switch"
                      id={"activo"}
                      checked={accessory.activo}
                      onChange={(e) => {
                        update("activo", e.target.checked);
                      }}
                      disabled={isReadOnly}
                    />
                  )}
                </Col>
                <Label sm={3}>
                  <FormattedMessage id="attribute.flagProduction" defaultMessage="Blocked for Production" />
                  <ProductInfo
                    formattedMessage={{ id: "attribute_info.flagProduction", defaultMessage: "Select this option if this product should not be visible on EasyVent production." }}
                  ></ProductInfo>
                </Label>
                <Col sm={3}>
                  {isReadOnly ? (
                    <Input type="text" plaintext readOnly value={accessory.flagProduction == 0} />
                  ) : (
                    <CustomInput
                      type="switch"
                      id={"flagProduction"}
                      checked={accessory.flagProduction == 0}
                      onChange={(e) => {
                        update("flagProduction", e.target.checked ? 0 : 1);
                      }}
                      disabled={isReadOnly}
                    />
                  )}
                </Col>
              </FormGroup>

              <FormGroup row className="px-4">
                <Label sm={3}>
                  <FormattedMessage id="attribute.codAccesorioComercial" defaultMessage="Accessory Code" />
                  <ProductInfo formattedMessage={{ id: "attribute_info.codAccesorioComercial", defaultMessage: "attribute_info.codAccesorioComercial" }}></ProductInfo>
                </Label>
                <Col sm={3}>
                  <Input
                    type="text"
                    id={"codAccesorioComercial"}
                    name={"codAccesorioComercial"}
                    className={`flex-grow-1 ${!isReadOnly ? "form-control" : ""} ${accessoryExists || accessory?.productoAccesorioComercial == "" ? "is-invalid" : ""}`}
                    required={true}
                    value={accessory.codAccesorioComercial}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^a-zA-Z0-9]/g, "");
                      update("codAccesorioComercial", val);
                    }}
                    onBlur={lookupAccessoryDescription}
                    plaintext={isReadOnly}
                    readOnly={isReadOnly}
                  />
                </Col>

                <Label sm={3}>
                  <FormattedMessage id="attribute.productoAccesorioComercial" defaultMessage="Accessory Description" />
                  <ProductInfo formattedMessage={{ id: "attribute_info.productoAccesorioComercial", defaultMessage: "attribute_info.productoAccesorioComercial" }}></ProductInfo>
                </Label>
                <Col sm={3}>
                  {accessoryNotFound ? (
                    <p className="text-danger mt-2">
                      <FormattedMessage id="accessory_information.accessory_not_found" defaultMessage="Accessory not found" />
                    </p>
                  ) : (
                    <Input
                      type="text"
                      id="productoAccesorioComercial"
                      name="productoAccesorioComercial"
                      className="flex-grow-1"
                      value={accessory.productoAccesorioComercial}
                      plaintext={true}
                      readOnly={true}
                    />
                  )}
                </Col>
              </FormGroup>

              <FormGroup row className="px-4">
                <Label sm={3}>
                  <FormattedMessage id="attribute.codCategoriaAccesorio" defaultMessage="Accessory Category" />
                  <ProductInfo formattedMessage={{ id: "attribute_info.codCategoriaAccesorio", defaultMessage: "attribute_info.codCategoriaAccesorio" }}></ProductInfo>
                </Label>
                <Col sm={3}>
                  {isReadOnly ? (
                    <Input type="text" plaintext readOnly value={accessory.codCategoriaAccesorio} />
                  ) : (
                    <Input type="select" id="codCategoriaAccesorio" value={accessory.codCategoriaAccesorio || ""} onChange={(e) => update("codCategoriaAccesorio", e.target.value)}>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </Input>
                  )}
                </Col>

                <Label sm={3}>
                  <FormattedMessage id="attribute.Tamany" defaultMessage="Size" />
                  <ProductInfo formattedMessage={{ id: "attribute.Tamany", defaultMessage: "attribute.Tamany" }}></ProductInfo>
                </Label>
                <Col sm={3}>
                  <Input
                    type="text"
                    id={"tamanyNominal"}
                    name={"tamanyNominal"}
                    className={`flex-grow-1 ${!isReadOnly ? "form-control" : ""}`}
                    value={accessory.tamanyNominal}
                    onChange={(e) => {
                      update("tamanyNominal", e.target.value);
                    }}
                    plaintext={isReadOnly}
                    readOnly={isReadOnly}
                  />
                </Col>
              </FormGroup>

              <Card>
                <CardHeader>
                  <FormattedMessage id="accessory_information.general_data" defaultMessage="General data" />
                </CardHeader>
                <CardBody className="p-3">
                  <FormGroup row className="px-4">
                    <Label sm={3}>
                      <FormattedMessage id="accessory_information.addPressureLosses" defaultMessage="Add pressure losses" />
                      <ProductInfo
                        formattedMessage={{ id: "accessory_information_info.addPressureLosses", defaultMessage: "accessory_information_info.addPressureLosses" }}
                      ></ProductInfo>
                    </Label>
                    <Col sm={3}>
                      {isReadOnly ? (
                        <Input type="text" plaintext readOnly value={accessory.hasPressureLosses} />
                      ) : (
                        <CustomInput
                          type="switch"
                          id={"hasPressureLosses"}
                          checked={accessory.hasPressureLosses}
                          onChange={(e) => {
                            update("hasPressureLosses", e.target.checked);
                          }}
                        />
                      )}
                    </Col>
                  </FormGroup>

                  {accessory.hasPressureLosses && (
                    <>
                      <FormGroup row className="px-4 mb-0">
                        <Label sm={3}>
                          <FormattedMessage id="attribute.preLossK0" defaultMessage="Factor k0" />
                          <ProductInfo formattedMessage={{ id: "attribute_info.preLossK0", defaultMessage: "attribute_info.preLossK0" }}></ProductInfo>
                        </Label>
                        <Col sm={3}>
                          <Input
                            type="number"
                            id={"preLossK0"}
                            name={"preLossK0"}
                            className={`flex-grow-1 ${!isReadOnly ? "form-control" : ""}`}
                            required={true}
                            value={accessory.preLossK0}
                            onChange={(e) => {
                              update("preLossK0", parseFloat(e.target.value));
                            }}
                            plaintext={isReadOnly}
                            readOnly={isReadOnly}
                          />
                        </Col>
                      </FormGroup>

                      <FormGroup row className="px-4 mb-0">
                        <Label sm={3}>
                          <FormattedMessage id="attribute.preLossK1" defaultMessage="Factor k1" />
                          <ProductInfo formattedMessage={{ id: "attribute_info.preLossK1", defaultMessage: "attribute_info.preLossK1" }}></ProductInfo>
                        </Label>
                        <Col sm={3}>
                          <Input
                            type="number"
                            id={"preLossK1"}
                            name={"preLossK1"}
                            className={`flex-grow-1 ${!isReadOnly ? "form-control" : ""}`}
                            required={true}
                            value={accessory.preLossK1}
                            onChange={(e) => {
                              update("preLossK1", parseFloat(e.target.value));
                            }}
                            plaintext={isReadOnly}
                            readOnly={isReadOnly}
                          />
                        </Col>

                        <Label sm={3}>
                          <FormattedMessage id="attribute.pressureLossUnit" defaultMessage="Pressure losses magnitude" />
                          <ProductInfo formattedMessage={{ id: "attribute_info.pressureLossUnit", defaultMessage: "attribute_info.pressureLossUnit" }}></ProductInfo>
                        </Label>
                        <Col sm={3}>
                          {isReadOnly ? (
                            <Input type="text" plaintext readOnly value={magnitudes[accessory.preLossKeyMagnitud]} />
                          ) : (
                            <Input type="select" id="preLossKeyMagnitud" value={accessory.preLossKeyMagnitud || ""} onChange={(e) => update("preLossKeyMagnitud", e.target.value)}>
                              {Object.keys(magnitudes).map((key) => (
                                <option key={key} value={key}>
                                  {magnitudes[key]}
                                </option>
                              ))}
                            </Input>
                          )}
                        </Col>
                      </FormGroup>

                      <FormGroup row className="px-4">
                        <Label sm={3}>
                          <FormattedMessage id="attribute.preLossK2" defaultMessage="Factor k2" />
                          <ProductInfo formattedMessage={{ id: "attribute_info.preLossK2", defaultMessage: "attribute_info.preLossK2" }}></ProductInfo>
                        </Label>
                        <Col sm={3}>
                          <Input
                            type="number"
                            id={"preLossK2"}
                            name={"preLossK2"}
                            className={`flex-grow-1 ${!isReadOnly ? "form-control" : ""}`}
                            required={true}
                            value={accessory.preLossK2}
                            onChange={(e) => {
                              update("preLossK2", parseFloat(e.target.value));
                            }}
                            plaintext={isReadOnly}
                            readOnly={isReadOnly}
                          />
                        </Col>
                      </FormGroup>
                    </>
                  )}
                  {accessory.codCategoriaAccesorio != 1 && (
                    <FormGroup row className="px-4 mb-0">
                      <Label sm={3}>
                        <FormattedMessage id="attribute.lugarAccesorio" defaultMessage="Accessory location" />
                        <ProductInfo formattedMessage={{ id: "attribute_info.lugarAccesorio", defaultMessage: "attribute_info.lugarAccesorio" }}></ProductInfo>
                      </Label>
                      <Col sm={3}>
                        {isReadOnly ? (
                          <Input type="text" plaintext readOnly value={accessory.lugarAccesorio} />
                        ) : (
                          <Input type="select" id="lugarAccesorio" value={accessory.lugarAccesorio || ""} onChange={(e) => update("lugarAccesorio", e.target.value)}>
                            <option value="GENERAL">{intl.formatMessage({ id: "accessory_position.general", defaultMessage: "General" })}</option>
                            <option value="INLET">{intl.formatMessage({ id: "accessory_position.inlet", defaultMessage: "Inlet" })}</option>
                            <option value="OUTLET">{intl.formatMessage({ id: "accessory_position.outlet", defaultMessage: "Outlet" })}</option>
                          </Input>
                        )}
                      </Col>
                    </FormGroup>
                  )}
                </CardBody>
              </Card>
              {accessory.codCategoriaAccesorio != 1 && (
                <div className="row mt-3">
                  <div className="col-6">
                    <Card>
                      <CardHeader>
                        <FormattedMessage id="accessory_information.product_association" defaultMessage="Product association" />
                      </CardHeader>
                      <CardBody className="p-3">
                        <FormGroup row className="px-4 mb-0">
                          <Col sm={12}>
                            <div className="d-flex align-items-center" style={{ gap: 10 }}>
                              <SuggestiveInput
                                suggester={productSAPSuggester}
                                inputProps={{
                                  value: searchSAP,
                                  required: false,
                                  onChange: (e) => {
                                    if (e.target.suggestion) {
                                      setSearchSAPSuggestion(e.target.suggestion);
                                      setSearchSAP(e.target.suggestion.code);
                                    } else {
                                      setSearchSAP(e.target.value);
                                    }
                                  },
                                }}
                                renderSuggestion={(s) => <div>{productSAPSuggester.resolveSuggestionValue(s)}</div>}
                                renderInputComponent={(p) => <Input {...p} />}
                              />
                              <button
                                type="button"
                                className="btn btn-outline-danger"
                                disabled={!searchSAP || searchSAP.length < 10}
                                onClick={() => {
                                  handleAddRelation(searchSAPSuggestion);
                                }}
                              >
                                <FormattedMessage id="accessory_information.add_relation" defaultMessage="Add relation" />
                              </button>
                            </div>
                          </Col>
                        </FormGroup>
                      </CardBody>
                    </Card>
                    {accessory.classGroups?.length > 0 && (
                      <Card style={{ marginTop: 10 }}>
                        <CardHeader>
                          <FormattedMessage id="accessory_information.existing_accessory_classes" defaultMessage="Existing accessory classes" />
                        </CardHeader>
                        <CardBody className="p-3" style={{ height: 242, overflowY: "auto" }}>
                          <FormGroup row className="px-4 mb-0">
                            <Col sm={12}>
                              <ListGroup>
                                {accessory.classGroups.map((rel) => (
                                  <ListGroupItem key={rel.claseAccesorio} className="d-flex justify-content-between align-items-center">
                                    {rel.claseAccesorio}
                                  </ListGroupItem>
                                ))}
                              </ListGroup>
                            </Col>
                          </FormGroup>
                        </CardBody>
                      </Card>
                    )}
                  </div>
                  <div className="col-6">
                    <Card>
                      <CardHeader>
                        <FormattedMessage id="accessory_information.product_relationships" defaultMessage="Product relationships" />
                      </CardHeader>
                      <CardBody className="p-3" style={{ height: 400, overflowY: "auto" }}>
                        <ListGroup>
                          {accessory.newClassGroups?.map((rel) => (
                            <ListGroupItem key={rel.codProductoComercial} className="d-flex justify-content-between align-items-center">
                              <span>
                                {rel.codProductoComercial} - {rel.productoComercial} ({rel.claseAccesorio})
                              </span>
                              <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleRemoveRelationship(rel)}>
                                Remove
                              </button>
                            </ListGroupItem>
                          ))}
                        </ListGroup>
                      </CardBody>
                    </Card>
                  </div>
                </div>
              )}

              {accessory.codCategoriaAccesorio == 1 && (
                <Card className="mt-3">
                  <CardHeader>
                    <FormattedMessage id="accessory_information.electrical_information" defaultMessage="Electrical information" />
                  </CardHeader>
                  <CardBody className="p-3">
                    <FormGroup row className="px-4">
                      <Label sm={3}>
                        <FormattedMessage id="attribute.mainSupply" defaultMessage="Mains Supply" />
                        <ProductInfo formattedMessage={{ id: "attribute_info.mainSupply", defaultMessage: "attribute_info.mainSupply" }}></ProductInfo>
                      </Label>
                      <Col sm={9}>
                        <MultiSelect
                          valueRenderer={customValueRenderer}
                          options={availableVoltages}
                          value={voltages}
                          hasSelectAll={false}
                          onChange={setVoltages}
                          labelledBy="Select"
                          overrideStrings={localisedStrings}
                        />
                      </Col>
                    </FormGroup>
                    <FormGroup row className="px-4">
                      <Label sm={3}>
                        <FormattedMessage id="attribute.flagRegulationTension" defaultMessage="Tension AC Regulator" />
                        <ProductInfo formattedMessage={{ id: "attribute_info.flagRegulationTension", defaultMessage: "attribute_info.flagRegulationTension" }}></ProductInfo>
                      </Label>
                      <Col sm={2}>
                        {isReadOnly ? (
                          <Input type="text" plaintext readOnly value={accessory.flagRegulationTension == 1} />
                        ) : (
                          <CustomInput
                            type="switch"
                            id={"flagRegulationTension"}
                            checked={accessory.flagRegulationTension == 1}
                            onChange={(e) => {
                              update("flagRegulationTension", e.target.checked ? 1 : 0);
                            }}
                          />
                        )}
                      </Col>
                      {accessory.flagRegulationTension == 1 && (
                        <Col sm={7}>
                          <Row>
                            <Label sm={3}>
                              <FormattedMessage id="attribute.min" defaultMessage="Min." /> (A)
                            </Label>
                            <Col sm={3}>
                              {isReadOnly ? (
                                <Input type="text" plaintext readOnly value={accessory.minimumAmps} />
                              ) : (
                                <Input
                                  type="number"
                                  id={"minimumAmps"}
                                  name={"minimumAmps"}
                                  className={`flex-grow-1 ${!isReadOnly ? "form-control" : ""}`}
                                  required={true}
                                  value={accessory.minimumAmps}
                                  onChange={(e) => {
                                    update("minimumAmps", parseFloat(e.target.value));
                                  }}
                                  plaintext={isReadOnly}
                                  readOnly={isReadOnly}
                                  placeholder="Minimum current (A)"
                                />
                              )}
                            </Col>
                            <Label sm={3}>
                              <FormattedMessage id="attribute.max" defaultMessage="Max." /> (A)
                            </Label>
                            <Col sm={3}>
                              {isReadOnly ? (
                                <Input type="text" plaintext readOnly value={accessory.maximumAmps} />
                              ) : (
                                <Input
                                  type="number"
                                  id={"maximumAmps"}
                                  name={"maximumAmps"}
                                  className={`flex-grow-1 ${!isReadOnly ? "form-control" : ""}`}
                                  required={true}
                                  value={accessory.maximumAmps}
                                  onChange={(e) => {
                                    update("maximumAmps", parseFloat(e.target.value));
                                  }}
                                  plaintext={isReadOnly}
                                  readOnly={isReadOnly}
                                  placeholder="Maximum current (A)"
                                />
                              )}
                            </Col>
                          </Row>
                        </Col>
                      )}
                    </FormGroup>

                    <FormGroup row className="px-4">
                      <Label sm={3}>
                        <FormattedMessage id="attribute.flagRegulationFrecuency" defaultMessage="Frequency Regulator" />
                        <ProductInfo formattedMessage={{ id: "attribute_info.flagRegulationFrecuency", defaultMessage: "attribute_info.flagRegulationFrecuency" }}></ProductInfo>
                      </Label>
                      <Col sm={2}>
                        {isReadOnly ? (
                          <Input type="text" plaintext readOnly value={accessory.flagRegulationFrecuency == 1} />
                        ) : (
                          <CustomInput
                            type="switch"
                            id={"flagRegulationFrecuency"}
                            checked={accessory.flagRegulationFrecuency == 1}
                            onChange={(e) => {
                              update("flagRegulationFrecuency", e.target.checked ? 1 : 0);
                            }}
                          />
                        )}
                      </Col>
                      {accessory.flagRegulationFrecuency == 1 && (
                        <Col sm={7}>
                          <Row>
                            <Label sm={3}>
                              <FormattedMessage id="attribute.min" defaultMessage="Min." /> (kW)
                            </Label>
                            <Col sm={3}>
                              {isReadOnly ? (
                                <Input type="text" plaintext readOnly value={accessory.minimumOutputPower} />
                              ) : (
                                <Input
                                  type="number"
                                  id={"minimumOutputPower"}
                                  name={"minimumOutputPower"}
                                  className={`flex-grow-1 ${!isReadOnly ? "form-control" : ""}`}
                                  required={true}
                                  value={accessory.minimumOutputPower}
                                  onChange={(e) => {
                                    update("minimumOutputPower", parseFloat(e.target.value));
                                  }}
                                  plaintext={isReadOnly}
                                  readOnly={isReadOnly}
                                  placeholder="Minimum output power (kW)"
                                />
                              )}
                            </Col>
                            <Label sm={3}>
                              <FormattedMessage id="attribute.max" defaultMessage="Max." /> (kW)
                            </Label>
                            <Col sm={3}>
                              {isReadOnly ? (
                                <Input type="text" plaintext readOnly value={accessory.maximumOutputPower} />
                              ) : (
                                <Input
                                  type="number"
                                  id={"maximumOutputPower"}
                                  name={"maximumOutputPower"}
                                  className={`flex-grow-1 ${!isReadOnly ? "form-control" : ""}`}
                                  required={true}
                                  value={accessory.maximumOutputPower}
                                  onChange={(e) => {
                                    update("maximumOutputPower", parseFloat(e.target.value));
                                  }}
                                  plaintext={isReadOnly}
                                  readOnly={isReadOnly}
                                  placeholder="Maximum output power (kW)"
                                />
                              )}
                            </Col>
                          </Row>
                        </Col>
                      )}
                    </FormGroup>

                    <FormGroup row className="px-4">
                      <Label sm={3}>
                        <FormattedMessage id="attribute.switchController" defaultMessage="Switch controller" />
                        <ProductInfo formattedMessage={{ id: "attribute_info.switchController", defaultMessage: "attribute_info.switchController" }}></ProductInfo>
                      </Label>
                      <Col sm={3}>
                        {isReadOnly ? (
                          <Input type="text" plaintext readOnly value={accessory.flagCommutador2} />
                        ) : (
                          <Input type="select" id="switchController" value={getSelectedSwitch()} onChange={handleSwitchChange}>
                            <option value="" disabled>
                              {intl.formatMessage({ id: "product_information.select_an_option", defaultMessage: "Select an option" })}
                            </option>
                            <option value="switch2">{intl.formatMessage({ id: "accessory_information.switch2", defaultMessage: "Switch 2 positions" })}</option>
                            <option value="switch3">{intl.formatMessage({ id: "accessory_information.switch3", defaultMessage: "Switch 3 positions" })}</option>
                            <option value="both">{intl.formatMessage({ id: "accessory_information.switch23", defaultMessage: "Switch 2 & 3 positions" })}</option>
                          </Input>
                        )}
                      </Col>
                    </FormGroup>

                    <FormGroup row className="px-4">
                      <Label sm={3}>
                        <FormattedMessage id="accessory_information.flagSwitch" defaultMessage="On / Off switch" />
                        <ProductInfo formattedMessage={{ id: "accessory_information_info.flagSwitch", defaultMessage: "accessory_information_info.flagSwitch" }}></ProductInfo>
                      </Label>
                      <Col sm={3}>
                        {isReadOnly ? (
                          <Input type="text" plaintext readOnly value={accessory.flagSwitch} />
                        ) : (
                          <CustomInput
                            type="switch"
                            id={"flagSwitch"}
                            checked={accessory.flagSwitch == 1}
                            onChange={(e) => {
                              update("flagSwitch", e.target.checked ? 1 : 0);
                            }}
                          />
                        )}
                      </Col>
                    </FormGroup>

                    <FormGroup row className="px-4">
                      <Label sm={3}>
                        <FormattedMessage id="accessory_information.flagControlEC" defaultMessage="Electronic controller" />
                        <ProductInfo
                          formattedMessage={{ id: "accessory_information_info.flagControlEC", defaultMessage: "accessory_information_info.flagControlEC" }}
                        ></ProductInfo>
                      </Label>
                      <Col sm={3}>
                        {isReadOnly ? (
                          <Input type="text" plaintext readOnly value={accessory.flagControlEC} />
                        ) : (
                          <CustomInput
                            type="switch"
                            id={"flagControlEC"}
                            checked={accessory.flagControlEC == 1}
                            onChange={(e) => {
                              update("flagControlEC", e.target.checked ? 1 : 0);
                            }}
                          />
                        )}
                      </Col>
                    </FormGroup>
                  </CardBody>
                </Card>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Modal isOpen={modalOpen} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal} className="pb-2">
          <FormattedMessage id="accessory_information.select_class" defaultMessage="Select Accessory Class" />
        </ModalHeader>
        <ModalBody className="py-0">
          {selectedProduct == null ? (
            <p className="text-danger">
              <FormattedMessage id="accessory_information.product_not_found" defaultMessage="Please check the entered product code." />
            </p>
          ) : (
            <>
              {accessoryExistsInBdP == false && (
                <p className="text-danger">
                  <FormattedMessage
                    id="accessory_information.code_not_available_in_bdp"
                    defaultMessage="This code is not available in the database. Please make sure the code is added so that relation is visible"
                  />
                </p>
              )}

              <h4>{selectedProduct?.description}</h4>
              {productsWithSameClass.length > 0 ? (
                <>
                  <p>
                    <FormattedMessage id="accessory_information.products_with_the_same_accessory_classes" defaultMessage="Products with the same accessory classes:" />
                  </p>
                  <div className="alert" style={{ border: "1px solid", maxHeight: 350, overflowY: "auto" }}>
                    <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                      {Object.entries(
                        productsWithSameClass.reduce((acc, p) => {
                          (acc[p.claseAccesorio] = acc[p.claseAccesorio] || []).push(p);
                          return acc;
                        }, {})
                      ).map(([claseAccesorio, products]) => (
                        <li key={claseAccesorio}>
                          <strong>{claseAccesorio}</strong>
                          <ul style={{ listStyle: "none", margin: 0, paddingLeft: 20 }}>
                            {products.map((p) => (
                              <li key={p.codProductoComercial}>
                                {p.codProductoComercial} - {p.productoComercial}
                              </li>
                            ))}
                          </ul>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <FormGroup tag="fieldset">
                    {[...new Set(productsWithSameClass.map((p) => p.claseAccesorio))].map((clase) => (
                      <FormGroup check key={clase}>
                        <Label check>
                          <Input
                            type="radio"
                            name="accessoryClass"
                            value={clase}
                            checked={accessoryClass === clase}
                            onChange={(e) => {
                              setAccessoryClass(e.target.value);
                              setNewAccessoryClass("");
                            }}
                          />
                          {clase}
                        </Label>
                      </FormGroup>
                    ))}

                    {/* <FormGroup check>
                      <Label check>
                        <Input type="radio" name="accessoryClass" value="new" checked={accessoryClass === "new"} onChange={() => setAccessoryClass("new")} />
                        <FormattedMessage id="accessory_information.new_accessory_class" defaultMessage="New Accessory Class" />
                      </Label>
                      {accessoryClass === "new" && (
                        <Input
                          className="mt-2"
                          type="text"
                          placeholder={intl.formatMessage({ id: "accessory_information.enter_new_accessory_class_name", defaultMessage: "Enter new accessory class name" })}
                          value={newAccessoryClass}
                          onChange={(e) => setNewAccessoryClass(replaceBlockedCharacters(e.target.value))}
                        />
                      )}
                    </FormGroup> */}

                    <button className="btn btn-primary" onClick={showNewAccessoryClass}>
                      <FormattedMessage id="accessory_information.new_accessory_class" defaultMessage="New Accessory Class" />
                    </button>
                  </FormGroup>
                </>
              ) : (
                <FormGroup className="mb-0">
                  {/* <label htmlFor="newAccessoryClass">
                    <FormattedMessage id="accessory_information.new_accessory_class" defaultMessage="New Accessory Class" />
                  </label> */}
                  {/* <Input
                    id="newAccessoryClass"
                    type="text"
                    placeholder={intl.formatMessage({ id: "accessory_information.enter_new_accessory_class_name", defaultMessage: "Enter new accessory class name" })}
                    value={newAccessoryClass}
                    disabled={accessoryClass != ""}
                    onChange={(e) => setNewAccessoryClass(replaceBlockedCharacters(e.target.value))}
                  /> */}
                  <button className="btn btn-primary" onClick={showNewAccessoryClass}>
                    <FormattedMessage id="accessory_information.new_accessory_class" defaultMessage="New Accessory Class" />
                  </button>
                </FormGroup>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          {selectedProduct && (
            <button className="btn btn-outline-danger" onClick={handleModalConfirm} disabled={!(accessoryClass || newAccessoryClass)}>
              <FormattedMessage id="app.ok" defaultMessage="OK" />
            </button>
          )}
          <button className="btn btn-outline-dark" onClick={toggleModal}>
            <FormattedMessage id="app.cancel" defaultMessage="Cancel" />
          </button>
        </ModalFooter>
      </Modal>
    </ValidationForm>
  );
}

export default AccessoryInformation;
